/**
 * "Filtrando por" + chips de drill e de filtro de coluna, cada um com × (mockup `chips`).
 * Remover um chip leva o foco ao × do próximo chip (ou do anterior, se era o último); sem chips sobrando, à busca
 * (`onEmptyFocus`) — o foco nunca cai no <body> quando o botão focado some.
 * Extraído de GoogleAdsV2/components/FilterChips.tsx.
 */
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { v } from './theme';
import './v2.css';

export interface FilterChip { id: string; text: string }

export function FilterChips<C extends FilterChip>({ chips, onRemove, onEmptyFocus, label = 'Filtrando por' }: { chips: C[]; onRemove: (c: C) => void; onEmptyFocus?: () => void; label?: string }) {
    const buttons = useRef(new Map<string, HTMLButtonElement>());
    /** Posição do chip removido, até a lista nova chegar (a URL muda de forma assíncrona). */
    const pending = useRef<{ index: number; ids: string } | null>(null);
    const ids = chips.map((c) => c.id).join('|');

    useEffect(() => {
        const p = pending.current;
        if (!p || p.ids === ids) return;
        pending.current = null;
        const next = chips[Math.min(p.index, chips.length - 1)];
        if (next) buttons.current.get(next.id)?.focus();
        else onEmptyFocus?.();
    }, [ids, chips, onEmptyFocus]);

    if (!chips.length) return null;
    return (
        <div role="group" aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
            <span aria-hidden style={{ fontSize: 12.5, color: v('faint') }}>{label}</span>
            {chips.map((c, i) => (
                <span key={c.id} title={c.text}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 6px 0 10px', borderRadius: 8, background: v('chip'), border: `1px solid ${v('border-2')}`, fontSize: 12.5, color: v('text'), maxWidth: 360 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.text}</span>
                    <button type="button" aria-label={`Remover filtro ${c.text}`} className="gads-chip-x"
                        ref={(el) => { if (el) buttons.current.set(c.id, el); else buttons.current.delete(c.id); }}
                        onClick={() => { pending.current = { index: i, ids }; onRemove(c); }}
                        style={{ width: 18, height: 18, flexShrink: 0, border: 0, borderRadius: 5, background: 'transparent', color: v('muted'), cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <X size={12} aria-hidden />
                    </button>
                </span>
            ))}
        </div>
    );
}
